import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { api, tokenStore, API_BASE } from "@/lib/api";
import { applyTenantBranding, resetBranding } from "@/lib/tenantFavicon";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(tokenStore.getUser());
  const [loading, setLoading] = useState(!!tokenStore.getAccess() && !user);

  const refreshMe = useCallback(async () => {
    if (!tokenStore.getAccess()) {
      setLoading(false);
      return null;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      tokenStore.set({ user: res.data });
      return res.data;
    } catch {
      setUser(null);
      tokenStore.clear();
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // Tenant-aware favicon: resolve via host (pre-auth) and from /tenant/branding
  // once the user is known. Falls back to the default Axia mark on logout.
  useEffect(() => {
    let alive = true;
    const hydrate = async () => {
      // Pre-auth: ask the backend what tenant matched the current host header.
      // (Returns {resolved:false} if none — keeps default branding.)
      try {
        const r = await axios.get(`${API_BASE}/tenant/resolve`);
        if (alive && r.data?.resolved) applyTenantBranding(r.data.tenant);
      } catch {
        /* ignore */
      }
      // Post-auth: definitive tenant branding from the user's workspace.
      if (user && tokenStore.getAccess()) {
        try {
          const r = await api.get("/tenant/branding");
          if (alive) applyTenantBranding(r.data);
        } catch {
          /* ignore */
        }
      }
    };
    hydrate();
    return () => {
      alive = false;
    };
  }, [user]);

  const login = (data) => {
    tokenStore.set(data);
    setUser(data.user);
  };

  const logout = async () => {
    const r = tokenStore.getRefresh();
    try {
      if (r) await api.post("/auth/logout", { refresh_token: r });
    } catch {
      /* ignore */
    }
    tokenStore.clear();
    setUser(null);
    resetBranding();
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function hasRole(user, ...roles) {
  if (!user) return false;
  if (user.roles?.includes("super_admin") || user.roles?.includes("admin")) return true;
  return roles.some((r) => user.roles?.includes(r));
}
